import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Phone, Mail, MapPin, Send, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { trackPageView, trackFormSubmit } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    city: '',
    product_required: '',
    quantity: '',
    message: '',
    promo_code: '',
    inquiry_type: 'bulk',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  useEffect(() => {
    trackPageView('/contact', 'Contact');
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Message sent! We\'ll get back to you soon.');
        trackFormSubmit('contact_inquiry', { type: formData.inquiry_type });
        setFormData({
          name: '', company: '', email: '', phone: '', city: '',
          product_required: '', quantity: '', message: '', promo_code: '', inquiry_type: 'bulk',
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Phone, title: 'Phone', value: '+91 8140 044100', href: 'tel:+918140044100' },
    { icon: Mail, title: 'Email', value: 'info@avicement.com', href: 'mailto:info@avicement.com' },
    { icon: MapPin, title: 'Location', value: 'Ahmedabad, Gujarat', href: '#' },
  ];

  const products = [
    'Lion Classic', 'Lion Supreme', 'Hathi Cement', 'Siddhee Cement',
    'Fly Ash Blocks', 'Sandbase Blocks', 'Cemtite Chemicals', 'Cemtite GGBS', 'Other'
  ];

  return (
    <div className="min-h-screen bg-void" data-testid="contact-page">
      <SEOHead
        title="Contact Us"
        description="Get in touch with Avi Cement Limited. Inquire about cement, AAC blocks, construction chemicals. Office: Ahmedabad. Phone: +91 8140 044100."
        path="/contact"
      />
      {/* Hero Section */}
      <section className="relative py-12 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <motion.div
          className="absolute top-1/2 left-1/4 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(243,100,88,0.1) 0%, transparent 60%)' }}
        />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <span className="text-coral font-mono text-sm mb-4 block">CONTACT US</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Let's talk<br />
              <span className="text-gradient">business</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Have questions or need a bulk quote? Our team is ready to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={ref} className="py-12 md:py-32 bg-surface">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
            >
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-8">
                Get in touch
              </h2>

              <div className="space-y-6 mb-12">
                {contactInfo.map((info) => (
                  <a
                    key={info.title}
                    href={info.href}
                    className="flex items-center gap-4 group"
                  >
                    <div className="w-12 h-12 bg-void rounded-xl flex items-center justify-center group-hover:bg-coral/20 transition-colors">
                      <info.icon className="w-5 h-5 text-coral" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">{info.title}</p>
                      <p className="text-white group-hover:text-coral transition-colors">{info.value}</p>
                    </div>
                  </a>
                ))}
              </div>

              {/* Office Hours */}
              <div className="bg-void border border-white/5 p-6 rounded-2xl">
                <h3 className="font-display font-semibold text-white mb-4">Office Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>Monday - Friday</span>
                    <span className="font-mono text-white">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Saturday</span>
                    <span className="font-mono text-white">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Sunday</span>
                    <span className="font-mono text-gray-500">Closed</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.2 }}
            >
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-8">
                Send us a message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-name"
                    className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none transition-colors"
                  />
                  <input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none transition-colors"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-email"
                    className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none transition-colors"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none transition-colors"
                  />
                </div>

                <select
                  name="product_required"
                  value={formData.product_required}
                  onChange={handleInputChange}
                  className="w-full bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white outline-none transition-colors cursor-pointer"
                >
                  <option value="">Select Product</option>
                  {products.map((product) => (
                    <option key={product} value={product}>{product}</option>
                  ))}
                </select>

                <textarea
                  name="message"
                  placeholder="Your Message *"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  data-testid="contact-message"
                  className="w-full bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none transition-colors resize-none"
                />

                <input
                  type="text"
                  name="promo_code"
                  placeholder="Promo Code (optional)"
                  value={formData.promo_code}
                  onChange={handleInputChange}
                  data-testid="contact-promo"
                  className="w-full bg-void border border-white/5 focus:border-coral rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none transition-colors text-sm"
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                  className="w-full flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium py-4 rounded-full transition-all disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      <Send className="w-5 h-5" /> Send Message
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
